import SearchIcon from "@mui/icons-material/Search";
import { Box, Card, Chip, Stack, SvgIcon, Typography } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { SelectComponent, SelectOption } from "../select";
import { useShopDetails } from "../../hooks/use-shop";
import { LIST_USERS } from "../../gql/queries";
import { useQuery } from "@apollo/client";
import { User } from "../../gql/graphql";
import LoadingButton from "@mui/lab/LoadingButton";
import { neutral } from "../../theme/colors";
import { palette } from "./report-theme";

interface ReportSearchProps {
	onSearch: (from: Dayjs, to: Dayjs, stylistUserId: string | undefined, timezone?: string) => void;
	loading?: boolean;
}

interface RangePreset {
	key: string;
	label: string;
	range: () => [Dayjs, Dayjs];
}

/**
 * A report is almost always read for one of a few standard windows. Presets make
 * those one click instead of two date pickers — and stop the page defaulting to
 * a single day, where every trend chart is a single dot.
 */
const presets: RangePreset[] = [
	{ key: 'today', label: 'Today', range: () => [dayjs().startOf('day'), dayjs().endOf('day')] },
	{
		key: 'yesterday',
		label: 'Yesterday',
		range: () => [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')],
	},
	{ key: '7d', label: 'Last 7 days', range: () => [dayjs().subtract(6, 'day').startOf('day'), dayjs().endOf('day')] },
	{ key: '30d', label: 'Last 30 days', range: () => [dayjs().subtract(29, 'day').startOf('day'), dayjs().endOf('day')] },
	{ key: 'mtd', label: 'This month', range: () => [dayjs().startOf('month'), dayjs().endOf('day')] },
	{
		key: 'lastMonth',
		label: 'Last month',
		range: () => [
			dayjs().subtract(1, 'month').startOf('month'),
			dayjs().subtract(1, 'month').endOf('month'),
		],
	},
	{ key: '12m', label: 'Last 12 months', range: () => [dayjs().subtract(11, 'month').startOf('month'), dayjs().endOf('day')] },
];

export const ReportSearch = (props: ReportSearchProps) => {
	const [from, setFrom] = useState<Dayjs>(dayjs(new Date()).startOf("day"));
	const [to, setTo] = useState<Dayjs>(dayjs(new Date()).endOf("day"));
	const [selectedUser, setSelectedUser] = useState<string | undefined>();
	const [selectedTimezone, setSelectedTimezone] = useState<string>('IST');
	const [activePreset, setActivePreset] = useState<string | undefined>('today');

	const { shopId, locationId } = useShopDetails();
	const { data } = useQuery(LIST_USERS, { variables: { shopId, locationId } });

	const usersSelectMap: SelectOption[] = useMemo(
		() => {
			/* '' is the "no filter" sentinel; mapped back to undefined on change. */
			const allUsers: SelectOption[] = [{ value: '', label: 'All' }];
			return allUsers.concat(
				data?.users?.map((user: User) => ({ value: user._id, label: `${user.firstName} ${user.lastName}` })) ?? []
			);
		},
		[data]
	);

	useEffect(() => {
		props.onSearch(from, to, selectedUser, selectedTimezone);
	}, []);

	const handleOnClick = () => {
		if (from! > to!) {
			toast.error("From date cannot be greater than To date");
			return;
		}
		props.onSearch(from, to, selectedUser, selectedTimezone);
	};

	/* A preset is a complete intent — apply it and run the query in one click. */
	const applyPreset = (preset: RangePreset) => {
		const [nextFrom, nextTo] = preset.range();
		setFrom(nextFrom);
		setTo(nextTo);
		setActivePreset(preset.key);
		props.onSearch(nextFrom, nextTo, selectedUser, selectedTimezone);
	};

	const timezoneOptions: any[] = useMemo(() => {
		return [
			{ value: 'IST', label: 'IST (India)' },
			{ value: 'UTC', label: 'UTC' },
			{ value: 'BANGLADESH', label: 'Bangladesh' },
			{ value: 'SRI_LANKA', label: 'Sri Lanka' },
		];
	}, []);

	return (
		<Card elevation={0} sx={{ border: `1px solid ${neutral[200]}`, borderRadius: 2 }}>
			<Stack spacing={2.5} sx={{ p: 2.5 }}>
				<Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
					<Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontWeight: 600 }}>
						QUICK RANGE
					</Typography>
					{presets.map((preset) => (
						<Chip
							key={preset.key}
							label={preset.label}
							size="small"
							onClick={() => applyPreset(preset)}
							sx={{
								borderRadius: 1.5,
								fontWeight: 500,
								backgroundColor: activePreset === preset.key ? palette.single : neutral[100],
								color: activePreset === preset.key ? '#fff' : 'text.primary',
								'&:hover': {
									backgroundColor: activePreset === preset.key ? palette.single : neutral[200],
								},
							}}
						/>
					))}
				</Stack>

				<Stack
					direction={{ xs: 'column', md: 'row' }}
					alignItems={{ xs: 'stretch', md: 'center' }}
					spacing={2}
					useFlexGap
					flexWrap="wrap"
				>
					<LocalizationProvider dateAdapter={AdapterDayjs}>
						<DatePicker
							label="From"
							value={from}
							slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
							onChange={(value) => {
								setFrom(value!);
								setActivePreset(undefined);
							}}
						/>
						<DatePicker
							label="To"
							value={to}
							slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
							onChange={(value) => {
								setTo(value!);
								setActivePreset(undefined);
							}}
						/>
					</LocalizationProvider>

					<SelectComponent
						sx={{ display: 'flex', alignItems: 'center', minWidth: 200, flexGrow: 1 }}
						data={usersSelectMap}
						selected={selectedUser ?? ''}
						label="Stylist"
						onChange={(value: string) => setSelectedUser(value || undefined)}
					/>

					<SelectComponent
						sx={{ display: 'flex', alignItems: 'center', minWidth: 180 }}
						data={timezoneOptions}
						selected={selectedTimezone}
						label="Timezone"
						onChange={(value: string) => setSelectedTimezone(value)}
					/>

					<Box sx={{ flexShrink: 0 }}>
						<LoadingButton
							loading={props.loading}
							startIcon={
								<SvgIcon>
									<SearchIcon />
								</SvgIcon>
							}
							variant="contained"
							onClick={handleOnClick}
						>
							Apply
						</LoadingButton>
					</Box>
				</Stack>
			</Stack>
		</Card>
	);
};
